import MUIAutocomplete from "@mui/material/Autocomplete";
import MUITextField from '@mui/material/TextField';
import User from "../../Utility/Crud/User";
import InfoItem from '../InfoItem';
import ReportSetting from "../ReportSetting";
import MainContext from "../../Context/MainContext/MainContext";

export default class ReportSettingUser extends ReportSetting {
    static contextType = MainContext; // For legacy class components

    /**
     * Initializes state with filtered users based on props.
     * If restrictToCurrentUser is true and only one user is available,
     * it sets that user as the selected user in settings.
     */
    componentDidMount() {
        const filteredUsers = this.getFilteredUsers();
        this.setState({
            filteredUsers: filteredUsers
        });
    }

    /**
     * Updates filtered users when restriction-related props change.
     * Invokes the parent update so local `settings` stays aligned with `props.settings`.
     * Dashboard widgets pass configuration after mount; without the parent update,
     * read-only summaries never receive `userprofileIds` from stored widget settings.
     * Also refreshes when application context finishes loading so `filteredUsers` is not
     * stuck empty from an early mount before `usersIndexed` was populated.
     */
    componentDidUpdate(previousProps, previousState) {
        super.componentDidUpdate(previousProps);

        const restrictionRelatedPropsChanged =
            previousProps.restrictToCurrentUser !== this.props.restrictToCurrentUser ||
            previousProps.showInactiveUsers !== this.props.showInactiveUsers ||
            previousProps.userRolesToFilter !== this.props.userRolesToFilter;

        const refreshedFilteredUsers = this.getFilteredUsers();
        const shouldReplaceEmptyFilteredUsers =
            (this.state.filteredUsers?.length ?? 0) === 0 &&
            refreshedFilteredUsers.length > 0;

        if (restrictionRelatedPropsChanged || shouldReplaceEmptyFilteredUsers) {
            this.setState({
                filteredUsers: refreshedFilteredUsers
            });
        }
    }

    /**
     * Filters users based on selected roles or restrictions.
     */
    getFilteredUsers() {
        let users;
        let condition;

        if (this.props.showInactiveUsers) {
            condition = [];
        } else {
            condition = [{
                key: "active",
                value: true
            }]
        }

        users = User.sort(
            User.read(
                this.context.usersIndexed,
                condition
            )
        ).filter(user => !user.username.includes("api-"));

        if (this.props.restrictToCurrentUser) {
            return this.filterUsersOnRestriction(users);
        }

        //ATP is a role filter that includes users with isAtp = true
        //removed from the list above to handle as a special case
        const userRolesToFilter = this.props.userRolesToFilter || [];
        let filteredUsers = users || [];
        if (userRolesToFilter.length > 0) {
            filteredUsers = users.filter(user =>
                (Array.isArray(user.role)
                    ? user.role.some(role => userRolesToFilter.includes(role))
                    : userRolesToFilter.includes(user.role)
                ) ||
                (userRolesToFilter.includes("ATP") && user.isAtp === true)
            );
        }
        return filteredUsers;
    }

    /**
     * Filters based on PCR links (roles seem too inconsistent to accurately use)
     * If no links act as normal restrict to current user and disable.
     * @param {*} users
     * @returns
     */
    filterUsersOnRestriction(users) {
        const currentUser = this.context.currentUser;
        const userIds = []
        // always push current user id
        userIds.push(currentUser.id);
        // find any links if they exist
        Object.values(this.context.salesPcrLinksIndexed).map((link) => {
            if (link.patientCareRepId === currentUser.id) {
                userIds.push(link.salesRepId);
            }
        }).filter(x => x);

        // Get inactiveUsers
        let inactiveUsers = users.filter((user) => user.active === false);
        // return available users.
        let usersAvailable = users.filter((user) => userIds.includes(user.id));
        // Combine the list of inactive users to the list of usersAvailable to user.
        const combinedUsers = new Set([...inactiveUsers, ...usersAvailable]);

        return Array.from(combinedUsers);

    }

    /**
     * Renders user dropdown for selecting multiple users.
    */
    renderUserDropdown() {
        const handleUserChange = (e, userprofiles) => {
            this.handleSettingChange("userprofileIds", userprofiles.map((userprofile) => userprofile.id));
        };

        const filteredUsers = this.state.filteredUsers || [];

        return (
            <MUIAutocomplete
                multiple
                options={filteredUsers}
                getOptionLabel={(user) => `${User.getDisplayName(user)}`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderOption={(props, user) => (
                    <li {...props} key={user.id}>
                        {User.getDisplayName(user)}
                    </li>
                )}
                disabled={filteredUsers.length === 1}
                value={User.read(
                    this.context.usersIndexed,
                    [{
                        key: "id",
                        value: filteredUsers.length === 1 ? filteredUsers[0].id : this.state.settings.userprofileIds || []
                    }]
                )}
                onChange={handleUserChange}
                sx={{ width: 500 }}
                renderInput={(params) => (
                    <MUITextField
                        {...params}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        label="User"
                        variant="standard"
                        InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                                <>
                                    {params.InputProps.startAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />
        );
    }

    renderContent() {
        return (
            <>
                {this.renderUserDropdown()}
            </>
        );
    }

    renderReadOnly() {
        const filteredUsers = this.state.filteredUsers || [];

        if (filteredUsers.length === 1) {
            const userName = User.getDisplayName(this.context.usersIndexed[filteredUsers[0].id]);
            return <InfoItem label="User" value={`${userName}`} />;
        }

        // all users relative to the current list of users available
        if (this.state.settings.userprofileIds?.length === 0) {
            return <InfoItem label="User" value="All Users" />;
        }

        const users = User.read(
            filteredUsers,
            [{ key: "id", value: this.state.settings.userprofileIds }]
        );

        const userNames = users.map(user => User.getDisplayName(user)).join(', ') || "All Users";
        return <InfoItem label="User" value={userNames} />;
    }
}